import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ProductSlider from '../components/ProductSlider';
import mainService from '../services/mainService';
import config from '../services/config.json';
import './Home.css';

const Home = () => {
	const [categories, setCategories] = useState([]);
	const [latestProducts, setLatestProducts] = useState([]);
	const [randomProducts, setRandomProducts] = useState([]);
	const [topSellingProducts, setTopSellingProducts] = useState([]);
	const [categoryProducts, setCategoryProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const categoriesScrollRef = useRef(null);
	const categoryRefs = useRef({});
	const [highlightCat, setHighlightCat] = useState(null);
	const location = useLocation();

	const scrollToLatestProducts = () => {
		document.getElementById('latest-products')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};

	// Mock data for fallback - expanded to show slider functionality
	const fallbackProducts = [];

	// Fetch latest products and random products from API
	useEffect(() => {
		const fetchProducts = async () => {
			try {
				setLoading(true);
				setError(null);
				const [categoriesResponse, latestResponse, randomResponse, topSellingResponse, categoriesWithProductsResponse] = await Promise.allSettled([
					mainService.categoriesInDropdown(),
					mainService.getLatestProducts(),
					mainService.getRandomProducts(),
					mainService.TopSellingProducts(),
					mainService.categoriesWithProducts()
				]);
				if (categoriesResponse.status === 'fulfilled' && categoriesResponse.value) {
					setCategories(categoriesResponse.value);
				}
				if (latestResponse.status === 'fulfilled' && latestResponse.value) {
					setLatestProducts(latestResponse.value);
				}
				if (randomResponse.status === 'fulfilled' && randomResponse.value) {
					setRandomProducts(randomResponse.value);
				}
				if (topSellingResponse.status === 'fulfilled' && topSellingResponse.value) {
					setTopSellingProducts(topSellingResponse.value);
				}
				if (categoriesWithProductsResponse.status === 'fulfilled' && categoriesWithProductsResponse.value) {
					setCategoryProducts(categoriesWithProductsResponse.value);
				}
			} catch (err) {
				console.error("Error in fetchProducts:", err);
				setError("Network error occurred. Please try again later.");
			} finally {
				setLoading(false);
			}
		};

		fetchProducts();
	}, []);

	useEffect(() => {
		const selected = location.state && location.state.selectedCategoryName;
		const wantsScroll = (location.state && location.state.scrollTo === 'shop-by-categories') || location.hash === '#shop-by-categories';
		if (!wantsScroll) return;
		const section = document.getElementById('shop-by-categories');
		if (section) {
			section.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
		if (selected) {
			setTimeout(() => {
				const container = categoriesScrollRef.current;
				const el = categoryRefs.current[selected];
				if (container && el) {
					const containerCenter = container.clientWidth / 2;
					const targetLeft = el.offsetLeft - containerCenter + el.clientWidth / 2;
					container.scrollTo({ left: Math.max(targetLeft, 0), behavior: 'smooth' });
					setHighlightCat(selected);
					setTimeout(() => setHighlightCat(null), 2000);
				}
			}, 200);
		}
	}, [location, categories]);

	const scrollCategories = (direction) => {
		const node = categoriesScrollRef.current;
		if (!node) return;
		const scrollAmount = Math.max(node.clientWidth * 0.8, 320);
		node.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
	};

	if (loading) {
		return (
			<div className="home">
				<div className="loading-container">
					<div className="loading-spinner"></div>
					<p>Loading products...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="home">
				<div className="error-container">
					<p className="error-message">{error}</p>
					<button 
						className="btn btn-primary" 
						onClick={() => window.location.reload()}
					>
						Try Again
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="home">
			{/* Hero Section */}
			<section className="hero">
				<div className="container">
					<div className="hero-content">
						<div className="hero-text">
							<h1 className="hero-title">
								Premium Mobile Back Covers
							</h1>
							<p className="hero-subtitle">
								Protective, stylish cases for iPhone, Samsung and more. Custom prints, premium materials, and fast UAE delivery.
							</p>
							<div className="hero-actions">
								<Link to="/products?id=0&type=all&name=all" className="btn btn-primary">
									Shop Cases
									<FaArrowRight className="btn-icon" />
								</Link>
								<Link to="/categories" className="btn btn-secondary">
									Explore Collections
									<FaArrowRight className="btn-icon" />
								</Link>
							</div>
						</div>
						<div className="hero-visual">
							<div className="floating-cards">
								<div className="hero-feature-track">
									<div className="floating-card card-1">
										<span className="card-icon">📱</span>
										<span className="card-text">iPhone Cases</span>
									</div>
									<div className="floating-card card-2">
										<span className="card-icon">🤖</span>
										<span className="card-text">Samsung Cases</span>
									</div>
									<div className="floating-card card-3">
										<span className="card-icon">🎨</span>
										<span className="card-text">Custom Prints</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<button
					type="button"
					className="hero-scroll"
					onClick={scrollToLatestProducts}
					aria-label="Scroll down to products"
				>
					<span className="hero-scroll-mouse" aria-hidden="true">
						<span className="hero-scroll-wheel" />
					</span>
					<span className="hero-scroll-chevron" aria-hidden="true" />
					<span className="hero-scroll-text">Scroll</span>
				</button>
			</section>

			{/* Latest Products Section - Using API Data */}
			<section id="latest-products" className="section latest-section">
				<div className="container">
					<ProductSlider
						allOnMobile
						products={latestProducts.length > 0 ? latestProducts : fallbackProducts}
						title="Latest Products"
						subtitle="Fresh arrivals just for you"
					/>
				</div>
			</section>

			{/* Shop By Category Section - Using Categories API Data */}
			<section id="shop-by-categories" className="section categories-hscroll-section">
				<div className="container">
					<div className="slider-header">
						<div className="slider-title-section">
							<h2 className="slider-title">
								Shop By Categories 
								<Link to="/categories?id=0&type=categories&name=all">
									<FaArrowRight className="title-arrow clickable" />
								</Link>
							</h2>
							<p className="slider-subtitle">Explore products by category</p>
						</div>
						<div className="slider-controls">
							<button className="slider-btn prev-btn" onClick={() => scrollCategories('left')} aria-label="Scroll categories left">
								<FaChevronLeft />
							</button>
							<button className="slider-btn next-btn" onClick={() => scrollCategories('right')} aria-label="Scroll categories right">
								<FaChevronRight />
							</button>
						</div>
					</div>
					<div className="categories-hscroll" role="list" ref={categoriesScrollRef}>
						{categories.map((cat) => (
							<Link
								key={cat.Id || cat.Name}
								to={`/products?id=${cat.Id || 0}&type=category&name=${encodeURIComponent(cat.Name || '')}`}
								className={`category-card-hs${highlightCat === (cat.Name || '') ? ' highlight' : ''}`}
								role="listitem"
								ref={(el) => {
									if (el && cat.Name) categoryRefs.current[cat.Name] = el;
								}}
							>
								<div className="category-icon-img">
									<img src={(cat.Icon ? (config.apiUrl + cat.Icon) : '')} alt={cat.Name || 'Category'} loading="lazy" decoding="async" />
								</div>
								<div className="category-name-text">{cat.Name || 'Category'}</div>
							</Link>
						))}
					</div>
				</div>
			</section>

			{/* Top Picks Section - Using Random Products API Data */}
			<section className="section latest-section">
				<div className="container">
					<ProductSlider
						products={randomProducts.length > 0 ? randomProducts : fallbackProducts}
						title="Top Picks For You"
						subtitle="Discover What Suits You Best"
					/>
				</div>
			</section>

			<section className="section featured-section">
				<div className="container">
					<ProductSlider
						products={topSellingProducts.length > 0 ? topSellingProducts : fallbackProducts}
						title="Top Selling Products"
						subtitle="The Hottest Picks Right Now"
					/>
				</div>
			</section>

      {categoryProducts.map((cat, idx) => {
				const catName = typeof cat.Name === 'string' ? cat.Name : (cat.Name && (cat.Name.Name ?? cat.Name.name)) ? String(cat.Name.Name ?? cat.Name.name) : `Category ${idx}`;
				return (
			<section className="section featured-section" key={cat.Id ?? catName ?? idx}>
				<div className="container">
					<ProductSlider
						products={Array.isArray(cat.Products) && cat.Products.length > 0 ? cat.Products : fallbackProducts}
						title={catName}
						subtitle=""
						arrowLink={`/products?id=${cat.Id || 0}&type=category&name=${encodeURIComponent(catName)}`}
					/>
				</div>
			</section>
				);
      })}

			{/* Features Section */}
			<section className="section features-section">
				<div className="container">
					<div className="section-header">
						<h2 className="section-title">Why Choose Byeilir?</h2>
						<p className="section-subtitle">We're committed to providing the best shopping experience</p>
					</div>
					<div className="features-grid">
						<div className="feature-card">
							<div className="feature-icon">🚚</div>
							<h3 className="feature-title">Fast Delivery</h3>
							<p className="feature-description">
								Get your orders delivered within 24-48 hours across the UAE
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-icon">🛡️</div>
							<h3 className="feature-title">Secure Shopping</h3>
							<p className="feature-description">
								Your data is protected with industry-standard security measures
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-icon">💰</div>
							<h3 className="feature-title">Best Prices</h3>
							<p className="feature-description">
								Competitive prices and regular discounts on premium products
							</p>
						</div>
						<div className="feature-card">
							<div className="feature-icon">🎯</div>
							<h3 className="feature-title">Quality Assured</h3>
							<p className="feature-description">
								All products are carefully selected and quality-tested
							</p>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
};

export default Home;
